import { faGear } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { COLOR_PICKER_POPUP_MARGIN, COLOR_PICKER_POPUP_TOTAL_HEIGHT, ColorPickerPopup, IColorPickerPopupPosition } from "components/colorpicker/popup/ColorPickerPopup";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import { IWebsiteBuilderIconAssetIdentity } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import { TWebsiteBuilderTableCell } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import { WEBSITE_BUILDER_Z_LAYERS } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderZLayers";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import React, { useLayoutEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderSelectIcon } from "../../../designSpace/views/hooks/helpers/WebsiteBuilderSelectIcon";
import { useTableBuilderContext } from "../../TableBuilderContext";
import { TableBuilderDropdown } from "../dropdown/TableBuilderDropdown";
import "./tableBuilderCellValueSettings.scss";

interface ITableBuilderCellValueSettingsProps {
  active: boolean;
  cell: TWebsiteBuilderTableCell;
}

export function TableBuilderCellValueSettings(
  props: ITableBuilderCellValueSettingsProps,
) {
  const { active, cell } = props;

  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={anchorRef}
      className="table-builder-cell-value-settings-anchor"
    >
      {active && (
        <TableBuilderCellValueSettingsContent
          anchorRef={anchorRef}
          cell={cell}
        />
      )}
    </div>
  );
}

interface ITableBuilderCellValueSettingsContentProps {
  anchorRef: React.MutableRefObject<HTMLDivElement>;
  cell: TWebsiteBuilderTableCell;
}

interface ITableBuilderCellValueSettingsPosition {
  x: number;
  y: number;
}

function TableBuilderCellValueSettingsContent(props: ITableBuilderCellValueSettingsContentProps) {
  const { anchorRef, cell } = props;

  const { t } = useTranslation();

  const { containerBuilderUIPortalId, operations, keyboardCommandsDisabledRef } = useTableBuilderContext();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [position, setPosition] = useState<ITableBuilderCellValueSettingsPosition>(null);

  const {
    state: selectIconModalState,
    updateModal: updateSelectIconModal,
    closeModal: closeSelectIconModal,
  } = useModal();

  useLayoutEffect(() => {
    keyboardCommandsDisabledRef.current = selectIconModalState.isOpen || colorPickerOpen;

    return () => {
      keyboardCommandsDisabledRef.current = false;
    };
  }, [selectIconModalState.isOpen, colorPickerOpen]);

  useLayoutEffect(() => {
    const portal = document.getElementById(containerBuilderUIPortalId);
    const anchor = anchorRef.current;

    if (anchor == null || portal == null) {
      return;
    }

    function updatePosition() {
      const anchorBoundingRect = anchor.getBoundingClientRect();
      const portalBoundingRect = portal.getBoundingClientRect();

      setPosition({
        x: anchorBoundingRect.x - portalBoundingRect.x,
        y: anchorBoundingRect.y - portalBoundingRect.y,
      });
    }

    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    const cell = anchor.parentElement;

    if (cell != null) {
      resizeObserver.observe(cell);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerBuilderUIPortalId]);

  function openSelectIconModal() {
    setDropdownOpen(false);
    updateSelectIconModal({ isOpen: true });
  }

  function handleSelectIcon(assetIdentity: IWebsiteBuilderIconAssetIdentity) {
    operations.setCellValue(cell.id, {
      type: "icon",
      assetIdentity,
      color: cell.value.type === "icon" ? cell.value.color : null,
    });

    closeSelectIconModal();
  }

  function switchToTextValue() {
    setDropdownOpen(false);
    operations.setCellValue(cell.id, { type: "text", content: "" });
  }

  function openIconColorPicker() {
    setDropdownOpen(false);
    setColorPickerOpen(true);
  }

  return (
    <>
      <Portal isMounted={true} parentId={containerBuilderUIPortalId}>
        <div
          className="table-builder-cell-value-settings"
          style={{
            left: position?.x,
            top: position?.y,
            visibility: position == null ? "hidden" : "visible",
            zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.containerBuilderUI,
          }}
        >
          <TableBuilderDropdown
            alignment="right"
            open={dropdownOpen}
            sections={[
              [
                {
                  label: cell.value.type === "text" ? t("Text (Selected)") : t("Switch to Text"),
                  disabled: cell.value.type === "text",
                  onClick: switchToTextValue,
                },
                {
                  label: cell.value.type === "icon" ? t("Icon (Selected)") : t("Switch to Icon"),
                  disabled: cell.value.type === "icon",
                  onClick: openSelectIconModal,
                },
              ],
              ...(cell.value.type === "icon"
                ? [[
                    { label: t("Change Icon"), onClick: openSelectIconModal },
                    { label: t("Change Icon Color"), onClick: openIconColorPicker },
                  ]]
                : []),
            ]}
            toggleDropdown={() => setDropdownOpen(open => !open)}
          >
            <OverlayTrigger
              placement="right"
              show={dropdownOpen ? false : undefined}
              delay={{
                show: 500,
                hide: 100,
              }}
              overlay={
                <Tooltip id={`table-builder-cell-value-settings-icon-${cell.id}`}>
                  <b>{t("Click")}</b> {t("to configure the cell value")}
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                className="table-builder-cell-value-settings-icon"
                icon={faGear}
              />
            </OverlayTrigger>
          </TableBuilderDropdown>
        </div>
      </Portal>

      <WebsiteBuilderSelectIcon
        assetIdentity={cell.value.type === "icon" ? cell.value.assetIdentity : null}
        isOpen={selectIconModalState.isOpen}
        closeModal={closeSelectIconModal}
        handleSelectIcon={handleSelectIcon}
      />

      {cell.value.type === "icon" && colorPickerOpen && (
        <TableBuilderCellIconColorPicker
          anchorRef={anchorRef}
          cellId={cell.id}
          assetIdentity={cell.value.assetIdentity}
          color={cell.value.color}
          closeColorPicker={() => setColorPickerOpen(false)}
        />
      )}
    </>
  );
}

interface ITableBuilderCellIconColorPickerProps {
  anchorRef: React.MutableRefObject<HTMLDivElement>;
  cellId: string;
  assetIdentity: IWebsiteBuilderIconAssetIdentity;
  color: string;
  closeColorPicker: () => void;
}

interface ITableBuilderCellIconColorPickerState {
  position: IColorPickerPopupPosition;
  color: string;
}

function TableBuilderCellIconColorPicker(props: ITableBuilderCellIconColorPickerProps) {
  const { anchorRef, cellId, assetIdentity, color, closeColorPicker } = props;

  const { operations } = useTableBuilderContext();

  const debouncedColorTimerRef = useRef<NodeJS.Timeout>(null);

  const [state, setState] = useState<ITableBuilderCellIconColorPickerState>(null);

  useLayoutEffect(() => {
    const cell = anchorRef.current?.parentElement;
    const boundingRect = cell?.getBoundingClientRect();

    if (boundingRect == null) {
      closeColorPicker();
      return;
    }

    const x = boundingRect.left + (boundingRect.width / 2);
    let y = window.innerHeight - COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    const topY = boundingRect.top - COLOR_PICKER_POPUP_TOTAL_HEIGHT;
    const bottomY = boundingRect.bottom + COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    if (bottomY <= window.innerHeight) {
      y = boundingRect.bottom + COLOR_PICKER_POPUP_MARGIN;
    } else if (topY >= 0) {
      y = topY;
    }

    setState({
      position: { x, y },
      color,
    });

    return () => clearTimeout(debouncedColorTimerRef.current);
  }, []);

  function updateColor(updatedColor: string) {
    clearTimeout(debouncedColorTimerRef.current);

    debouncedColorTimerRef.current = setTimeout(() => {
      operations.setCellValue(cellId, { type: "icon", assetIdentity, color: updatedColor });
    }, 500);

    setState(prev => ({
      ...prev,
      color: updatedColor,
    }));
  }

  if (state != null) {
    return (
      <ColorPickerPopup
        position={state.position}
        hexColor={state.color}
        updateHexColor={updateColor}
        closePopup={closeColorPicker}
        portalParentId={WEBSITE_BUILDER_PORTAL}
      />
    );
  } else {
    return null;
  }
}